/**
 * This service manages "Kartleggingen".
 * Kartleggingen consists of questions within categories.
 *
 * The state is managed mainly by four signals:
 * - `currentQuestionIndex`
 * - `currentCategoryIndex`
 * - `formsIndex`
 * - `_shouldShowCategoryIntro`
 *
 * All other state is derived (computed) from these signals, and the json api-response with questions.
 * `currentQuestionIndex` tracks the current index into the questions in the current category.
 * `currentCategoryIndex` tracks the current index into the categories
 * Answers to questions are collected into `forms`, where `formsIndex` is the answers to the current question.
 *
 * At the start of every category of questions, an introduction screen should be displayed, introducing the category.
 * This is handled by setting `_shouldShowCategoryIntro` to true.
 * The component (consumer) will call `beginNextCategory()` to begin displaying and answering the questions belonging to the category.
 */

import { computed, inject, Injectable, Signal, signal, WritableSignal } from "@angular/core";
import { SporsmalsType } from "../../pages/kartlegging/types/SporsmalsType";
import { FeltAlternativ, FormRoot } from "@husbanken/designsystem-angular-forms";
import { FormArray, FormControl, Validators } from "@angular/forms";
import { HttpClient } from "@angular/common/http";
import { Observable, tap } from "rxjs";
import { SporreundersokelseTO } from "../../dto/SporreundersokelseTO";
import { KategoriTO } from "../../dto/KategoriTO";
import { SporsmalTO } from "../../dto/SporsmalTO";
import { SvarTO } from "../../dto/SvarTO";
import { BesvarelseTO } from "../../dto/BesvarelseTO";
import { ColorThemeService } from "../colortheme/ColorThemeService";
import { getColor } from "../../pages/kartlegging/types/ColorTheme";

@Injectable()
export class KartleggingService {
  private http: HttpClient = inject(HttpClient);
  private colorThemeService: ColorThemeService = inject(ColorThemeService);

  private data: WritableSignal<SporreundersokelseTO> = signal({ kategorier: [] });
  private currentQuestionIndex: WritableSignal<number> = signal(0);
  private currentCategoryIndex: WritableSignal<number> = signal(0);

  // forms is represented as a flat array of FormRoot, with as many slots as total possible amount of questions
  private totalNumberOfQuestions: Signal<number> = computed(() => this.data().kategorier.reduce((acc: number, cat) => acc + cat.sporsmal.length, 0));
  private forms: FormRoot<any>[] = Array(this.totalNumberOfQuestions()).fill(undefined);
  private formsIndex: WritableSignal<number> = signal(0);

  public load(): Observable<SporreundersokelseTO> {
    return this.http.get<SporreundersokelseTO>("api/sporreundersokelse").pipe(
      tap(data => {
        this.data.set(data);
        this.initialize();
      }),
    );
  }

  /**
   * Initialize required state for displaying "Kartlegging"
   * Must be called *after* fetching data from backend
   */
  private initialize(): void {
    this.currentQuestionIndex.set(0);
    this.currentCategoryIndex.set(0);
    this.formsIndex.set(0);
    // Reset any answers left over from a previous visit. The service is route-scoped,
    // so its instance is cached and reused across navigations (Angular never destroys
    // route-provider injectors); clearing here guarantees a fresh form set each load.
    this.forms = Array(this.totalNumberOfQuestions()).fill(undefined);
    this._shouldShowCategoryIntroduction.set(true);
    this.createFormForCurrentQuestion();
    this.updateTheme();
  }

  /**
   * Clean up state that outlives the "Kartlegging" page.
   * Must be invoked by the consumer on component destroy, because Angular does not
   * destroy route-scoped provider injectors when navigating away.
   */
  public cleanup(): void {
    this.colorThemeService.resetTheme();
  }

  private readonly currentCategory: Signal<KategoriTO> = computed(() => this.data().kategorier[this.currentCategoryIndex()]);
  readonly currentQuestion: Signal<SporsmalTO> = computed(() => this.currentCategory().sporsmal[this.currentQuestionIndex()]);
  readonly questionImageUri: Signal<string> = computed(() => this.currentQuestion().image?.uri);
  readonly questionImageType: Signal<string> = computed(() => this.currentQuestion().image?.type);

  readonly currentCategoryNumber: Signal<number> = computed(() => this.currentCategoryIndex() + 1);
  readonly currentCategoryId: Signal<number> = computed(() => this.currentCategory()?.id);
  readonly totalNumberOfCategories: Signal<number> = computed(() => this.data().kategorier.length);
  readonly categoryTitle: Signal<string> = computed(() => this.currentCategory().navn);
  readonly categoryDescription: Signal<string> = computed(() => this.currentCategory().beskrivelse);
  readonly categoryImageUri: Signal<string> = computed(() => this.currentCategory().image?.uri);
  readonly categoryImageType: Signal<string> = computed(() => this.currentCategory().image?.type);
  readonly categoryIconUri: Signal<string> = computed(() => this.currentCategory().icon?.uri);
  readonly categoryIconType: Signal<string> = computed(() => this.currentCategory().icon?.type);
  readonly currentQuestionNumber: Signal<number> = computed(() => this.currentQuestionIndex());
  readonly totalNumberOfQuestionsInCategory: Signal<number> = computed(() => this.currentCategory().sporsmal.length - 1);

  readonly currentForm: Signal<FormRoot<any>> = computed(() => this.forms[this.formsIndex()]);
  readonly currentAlternatives: Signal<FeltAlternativ[]> = computed(() =>
    this.currentQuestion().svarAlternativer.map(alt => ({
      key: alt.id.toString(),
      tekst: alt.tekst,
    })),
  );

  // Invariant: Never extends beyond final question
  readonly isFinalQuestion: Signal<boolean> = computed(() => {
    if (this.currentCategoryIndex() < this.data().kategorier.length - 1) {
      // Assuming categories have at least one question
      return false;
    } else {
      return this.currentQuestionIndex() === this.currentCategory().sporsmal.length - 1;
    }
  });

  // Always show previous button unless current screen is intro screen to first category
  readonly showPreviousButton: Signal<boolean> = computed(() => {
    return !(this.shouldShowCategoryIntroduction() && this.currentCategoryIndex() == 0);
  });

  private _shouldShowCategoryIntroduction: WritableSignal<boolean> = signal(true);
  readonly shouldShowCategoryIntroduction: Signal<boolean> = this._shouldShowCategoryIntroduction.asReadonly();

  // Initialize a new form(root) for the current question
  // Does not create a new FormRoot if the spot is already populated
  // Important to update `currentCategoryIndex` and `currentQuestionIndex` before calling this function
  // as the slot looked at is derived from these.
  private createFormForCurrentQuestion(): void {
    // A FormRoot might already have been initialized
    // if the user can navigate backwards and forwards in questions
    if (this.forms[this.formsIndex()] !== undefined) {
      return;
    }

    switch (this.currentQuestion().sporsmalsType) {
      case SporsmalsType.Envalg: {
        const control = { control: new FormControl(undefined, Validators.required) };
        this.forms[this.formsIndex()] = new FormRoot(control);
        break;
      }
      case SporsmalsType.Flervalg: {
        const controls = { control: new FormArray([]) };
        this.forms[this.formsIndex()] = new FormRoot(controls);
        break;
      }
    }
  }

  // Each category has a specific colorTheme
  // Update theme to be correct for current category
  private updateTheme(): void {
    this.colorThemeService.setTheme(getColor(this.currentCategory().id));
  }

  public submitKartlegging(): Observable<string> {
    const answers: number[][] = this.forms.map(form => {
      const value: string | [string] = form.value.control;
      if (Array.isArray(value)) {
        return value.map(v => Number(v));
      }
      return [Number(value)];
    });
    const sporsmalIds: number[] = this.data()
      .kategorier.flatMap(kategori => kategori.sporsmal)
      .map(sporsmal => sporsmal.id);

    const svar: SvarTO[] = answers.map((value, index) => {
      return {
        sporsmalId: sporsmalIds[index],
        svarId: value,
      };
    });
    const besvarelse: BesvarelseTO = { besvarelse: svar };
    // postBesvarelse til backend
    const baseUrl = "api/besvarelse";

    return this.http.post(baseUrl, besvarelse, {
      responseType: "text",
    });
  }

  public beginNextCategory(): void {
    this._shouldShowCategoryIntroduction.set(false);
  }

  public previousQuestion(): void {
    if (this.currentQuestionIndex() !== 0) {
      this.currentQuestionIndex.update(n => n - 1);
      this.formsIndex.update(n => n - 1);
      return;
    }

    if (!this._shouldShowCategoryIntroduction()) {
      this._shouldShowCategoryIntroduction.set(true);
      return;
    }

    if (this.currentCategoryIndex() !== 0) {
      this._shouldShowCategoryIntroduction.set(false);
      this.currentCategoryIndex.update(n => n - 1);
      this.currentQuestionIndex.set(this.currentCategory().sporsmal.length - 1);
      this.formsIndex.update(n => n - 1);

      this.updateTheme();
    }
  }

  // Advances to next question
  public nextQuestion(): void {
    // Absolute final question
    if (this.isFinalQuestion()) {
      return;
    }

    // Final question in category
    if (this.currentQuestionIndex() === this.currentCategory().sporsmal.length - 1) {
      this.currentCategoryIndex.update(n => n + 1);
      this.currentQuestionIndex.set(0);
      this._shouldShowCategoryIntroduction.set(true);

      this.formsIndex.update(n => n + 1);
      this.createFormForCurrentQuestion();

      this.updateTheme();
      return;
    }

    this.currentQuestionIndex.update(n => n + 1);
    this.formsIndex.update(n => n + 1);
    this.createFormForCurrentQuestion();
  }
}
